'use client';

import { forwardRef } from 'react';
import type { ColorSwatchProps as MantineColorSwatchProps } from '@mantine/core';
import { ColorSwatch as MantineColorSwatch } from '@mantine/core';

import type { LockedProps } from '../types';

export type ColorSwatchProps = Omit<MantineColorSwatchProps, LockedProps> & {
  /**
   * Element to render as. `span` keeps the swatch valid inside phrasing content, where Mantine's
   * default `div` would nest a block element in the `p` a `Text` renders.
   *
   * Narrowed to these two rather than made fully polymorphic with `LockedPolymorphic`: a swatch is
   * only ever a box of colour, so `span` is the one alternative it needs, and the narrow union keeps
   * the prop surface minimal.
   */
  component?: 'div' | 'span';
};

// Two concessions to Mantine's polymorphic overloads, which cannot resolve a `'div' | 'span'` union:
// `component` is passed as its own attribute rather than left in the spread, and the ref stays typed
// to the default `div` — no caller refs a `span` swatch, and widening it fails to compile.
export const ColorSwatch = forwardRef<HTMLDivElement, ColorSwatchProps>(
  ({ component = 'div', ...props }, ref) => {
    return <MantineColorSwatch {...props} component={component} ref={ref} />;
  },
);

ColorSwatch.displayName = 'ColorSwatch';
